// Tiny inline-link format for strings that live in i18n JSON: `[visible text](target)`.
//
// - Plain strings stay valid: a string with no link markup parses to one text segment.
// - The visible text may contain parentheses and any language (it is just the characters
//   between the brackets) but no square brackets.
// - The target has no whitespace or parentheses. Only a site path (`/saved-words`,
//   `/lessons?lesson=3.25`) or an `https://` URL is accepted; anything else
//   (`javascript:`, `http:`, relative paths, protocol-relative `//host`) is left as the
//   literal text so a typo can never turn into a surprising link.
// - Anything that is not a complete, valid link (unbalanced brackets, a missing `)`,
//   empty link text) falls back to plain text, unchanged.

export type InlineSegment =
    | { type: "text"; text: string }
    | { type: "link"; text: string; href: string; external: boolean };

const LINK_PATTERN = /\[([^[\]]+)\]\(([^()\s]+)\)/g;

function isAllowedHref(href: string): boolean {
    if (href.startsWith("https://")) return href.length > "https://".length;
    return href.startsWith("/") && !href.startsWith("//") && !href.includes("\\");
}

export function parseInlineLinks(input: string): InlineSegment[] {
    const segments: InlineSegment[] = [];
    // Text that has not been emitted yet. Invalid link syntax is appended here (rather than
    // emitted on its own) so the output never contains two text segments in a row.
    let pending = "";
    let cursor = 0;
    const pattern = new RegExp(LINK_PATTERN.source, "g");
    let match = pattern.exec(input);
    while (match !== null) {
        const [raw, text, href] = match;
        pending += input.slice(cursor, match.index);
        cursor = match.index + raw.length;
        if (text.trim() !== "" && isAllowedHref(href)) {
            if (pending !== "") segments.push({ type: "text", text: pending });
            pending = "";
            segments.push({ type: "link", text, href, external: href.startsWith("https://") });
        } else {
            pending += raw;
        }
        match = pattern.exec(input);
    }
    pending += input.slice(cursor);
    if (pending !== "") segments.push({ type: "text", text: pending });
    return segments;
}

/** The visible text of a string once any link markup is removed. */
export function stripInlineLinks(input: string): string {
    return parseInlineLinks(input)
        .map((segment) => segment.text)
        .join("");
}
